import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  FormControl,
  FormLabel,
  Textarea,
  HStack,
  useToast,
} from '@chakra-ui/react'
import { Icon } from './Icon'
import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useAccount } from 'wagmi'
import { logType, LogType, useEIP712Sign } from '@provex/eip712-utils'
import { sacrificeApi } from '../config/sacrifice-api'
import { SacrificeData } from 'sacrifice-utils/types'
import { ProjectNetworkIdentifier } from '@provex/eip712-utils/keys'
import type { SignReportInputs } from '@provex/eip712-utils/hooks'
import { useProjects } from '../contexts'

interface ReportModalProps {
  onClose: () => void
  target: ProjectNetworkIdentifier | SacrificeData | null
  title?: string
  openAppKit?: ReturnType<(typeof import('@reown/appkit/react'))['useAppKit']>['open']
}

const ReportModal = ({
  onClose,
  target,
  title = 'Submit Report',
  openAppKit,
}: ReportModalProps) => {
  const toast = useToast()
  const { projectId } = useProjects()

  // Use the first available project as fallback if no projectId is set
  const effectiveProjectId = useMemo(() => {
    return projectId || ''
  }, [projectId])
  const [reportDescription, setReportDescription] = useState('')
  const { address, isConnected } = useAccount()
  const { signReport } = useEIP712Sign()
  const sacrifice = useMemo(() => target as SacrificeData, [target])
  const isOpen = useMemo(() => !!target, [target])
  const isGeneralReport = useMemo(() => !sacrifice || !sacrifice.blockHash, [sacrifice])

  // Fallback: if openAppKit is not provided, create a no-op function
  const open =
    openAppKit ||
    (() => {
      console.warn('AppKit not loaded yet')
      toast({
        title: 'Wallet Connection Unavailable',
        description: 'Please refresh the page and try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    })

  // Mutation for submitting reports
  const submitReportMutation = useMutation({
    mutationFn: async () => {
      const timestampMs = Date.now()

      const reportInputs = {
        projectId: effectiveProjectId,
        networkType: target!.networkType,
        chainId: BigInt(target!.chainId),
        blockHash: '',
        tokenAddress: '',
        logIndex: 0,
        logType: logType.NATIVE,
        transactionHash: '',
        message: reportDescription.trim(),
        timestamp: BigInt(timestampMs),
      } as SignReportInputs

      if (!isGeneralReport) {
        const t = target as SacrificeData
        reportInputs.blockHash = t.blockHash
        reportInputs.tokenAddress = t.tokenAddress
        reportInputs.logIndex = t.logIndex
        reportInputs.logType = t.logType as LogType
        reportInputs.transactionHash = t.transactionHash
      }

      // Create EIP-712 signature for the report
      const signature = await signReport(reportInputs)

      if (!signature) {
        throw new Error('Failed to sign report')
      }

      const requestData = {
        ...reportInputs,
        chainId: BigInt(reportInputs.chainId),
        timestamp: BigInt(timestampMs),
      }

      return await sacrificeApi.reports.submit({
        reportData: {
          inputs: requestData,
          signature,
        },
        signal: undefined,
      })
    },
    onSuccess: result => {
      console.log('Report submitted successfully:', result)
      toast({
        title: 'Report Submitted',
        description: 'Thank you for your report. We will review it and get back to you soon.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
      setReportDescription('')
      onClose()
    },
    onError: (error: Error) => {
      console.error('Report submission failed:', error)
      toast({
        title: 'Submission Failed',
        description:
          error.message || 'There was an error submitting your report. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    },
  })

  const handleSubmit = () => {
    if (!reportDescription.trim() || !target) {
      toast({
        title: 'Missing Information',
        description: 'Please provide a description for your report.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    if (!isConnected || !address) {
      // Trigger wallet connection
      open({ view: 'Connect' })
      return
    }

    submitReportMutation.mutate()
  }

  const isSubmitDisabled = !reportDescription.trim()

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent borderRadius="none">
        <ModalHeader>
          <HStack>
            <Icon icon={isGeneralReport ? 'mdi:email-outline' : 'mdi:alert-circle-outline'} />
            <Text>{title}</Text>
          </HStack>
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody py={0}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="text.muted">
              {isGeneralReport
                ? 'Report problems or request assistance. Authentication required via wallet signature.'
                : `Please describe the issue you're experiencing with this sacrifice:`}
            </Text>
            {!sacrifice ? null : (
              <VStack spacing={1} w="full">
                <HStack gridColumn={2} w="full">
                  <Text fontSize="xs" color="text.muted" fontFamily="mono">
                    Project Id: {sacrifice.projectId}
                  </Text>
                  <Text fontSize="xs" color="text.muted" fontFamily="mono">
                    Network Type: {sacrifice.networkType}
                  </Text>
                  <Text fontSize="xs" color="text.muted" fontFamily="mono">
                    Chain Id: {sacrifice.chainId}
                  </Text>
                </HStack>
                {!isGeneralReport && (
                  <>
                    <Text fontSize="xs" color="text.muted" fontFamily="mono">
                      Block Hash: {sacrifice.blockHash}
                    </Text>
                    <Text fontSize="xs" color="text.muted" fontFamily="mono">
                      Transaction Hash: {sacrifice.transactionHash}
                    </Text>
                    <HStack gridColumn={2} w="full">
                      <Text fontSize="xs" color="text.muted" fontFamily="mono">
                        Log Index: {sacrifice.logIndex}
                      </Text>
                      <Text fontSize="xs" color="text.muted" fontFamily="mono">
                        Log Type: {sacrifice.logType}
                      </Text>
                    </HStack>
                  </>
                )}
              </VStack>
            )}

            <FormControl>
              <FormLabel fontSize="sm">Description</FormLabel>
              <Textarea
                placeholder={
                  isGeneralReport
                    ? 'Please provide detailed information about your issue, including any error messages, steps to reproduce, or other relevant details...'
                    : 'Describe the issue (e.g., incorrect amount, wrong token, pricing error, etc.)'
                }
                value={reportDescription}
                onChange={e => setReportDescription(e.target.value)}
                rows={6}
                fontSize="sm"
              />
            </FormControl>

            {!isConnected && (
              <Text fontSize="sm" color="brand.accent">
                Please connect your wallet to submit a report.
              </Text>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="flame"
            onClick={handleSubmit}
            isDisabled={isSubmitDisabled}
            isLoading={submitReportMutation.isPending}
            loadingText="Submitting..."
          >
            {isConnected ? 'Submit Report' : 'Connect Wallet'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default ReportModal
